import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { wsUrl, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DominoTile } from "@/components/DominoTile";
import { DominoBoard } from "@/components/DominoBoard";
import { toast } from "sonner";
import { Loader2, LogOut, ShoppingCart, SkipForward, Send, Volume2, VolumeX, Wifi, WifiOff, Trophy, Copy, Bot } from "lucide-react";

function beep(freq = 660) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.frequency.value = freq; o.type = "sine";
    g.gain.setValueAtTime(0.08, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    o.start(); o.stop(ctx.currentTime + 0.25);
  } catch {}
}

export default function Game() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const pmode = location.state?.mode;
  const pStake = location.state?.stake;
  const pCurrency = location.state?.currency || "real";
  const pName = location.state?.name || "";
  const pPassword = location.state?.password || "";
  const pCode = location.state?.code || "";
  const stake = params.get("stake");
  const currency = params.get("currency") || "real";

  const wsRef = useRef(null);
  const closingRef = useRef(false);
  const inMatchRef = useRef(false);
  const finishedRef = useRef(false);
  const [phase, setPhase] = useState("connecting"); // connecting|queue|playing|finished
  const [connected, setConnected] = useState(false);
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState(null);
  const [drag, setDrag] = useState(null); // {tile, x, y, over: 'left'|'right'|null}
  const dragRef = useRef(null);
  const boardRef = useRef(null);
  const [chat, setChat] = useState([]);
  const [chatText, setChatText] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [result, setResult] = useState(null);
  const [rematch, setRematch] = useState(null); // null | 'waiting' | 'requested'
  const [exitIn, setExitIn] = useState(null);
  const [roundEnd, setRoundEnd] = useState(null);
  const [oppOffline, setOppOffline] = useState(null);
  const [privateCode, setPrivateCode] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [sound, setSound] = useState(true);
  const soundRef = useRef(true);
  useEffect(() => { soundRef.current = sound; }, [sound]);
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  const send = useCallback((obj) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify(obj));
  }, []);

  const connect = useCallback(() => {
    const token = localStorage.getItem("dr_token");
    if (!token) { navigate("/"); return; }
    closingRef.current = false;
    if (wsRef.current) { try { wsRef.current.onclose = null; wsRef.current.close(); } catch (e) { /* noop */ } }
    const ws = new WebSocket(wsUrl(token));
    wsRef.current = ws;

    ws.onopen = () => {
      if (wsRef.current !== ws) return;
      setConnected(true);
      if (finishedRef.current) return; // partida encerrada: aguardando revanche/lobby, não re-entrar na fila
      if (inMatchRef.current) { send({ action: "rejoin" }); return; }
      if (pmode === "create_private") send({ action: "create_private_room", stake: parseFloat(pStake), currency: pCurrency, name: pName, password: pPassword });
      else if (pmode === "join_private") send({ action: "join_private_room", code: pCode, password: pPassword });
      else if (pmode === "bot") send({ action: "join_bot" });
      else if (stake) send({ action: "join_queue", stake: parseFloat(stake), currency });
      else send({ action: "rejoin" });
    };
    ws.onclose = () => {
      if (wsRef.current !== ws) return;
      setConnected(false);
      if (!closingRef.current) setTimeout(() => connect(), 2000);
    };
    ws.onmessage = (ev) => {
      if (wsRef.current !== ws) return;
      const m = JSON.parse(ev.data);
      const uid = userRef.current?.user_id;
      switch (m.event) {
        case "queue_joined": setPhase("queue"); break;
        case "private_created": setPrivateCode(m.code); setPhase("queue"); break;
        case "private_cancelled": break;
        case "queue_left": break;
        case "match_found": inMatchRef.current = true; finishedRef.current = false; setPhase("playing"); setResult(null); setRoundEnd(null); setRematch(null); break;
        case "state":
          inMatchRef.current = true;
          setState(m.state);
          if (m.state.status === "playing") setPhase("playing");
          break;
        case "turn_changed":
          if (m.current_turn === uid && soundRef.current) beep(720);
          break;
        case "tile_played": if (soundRef.current) beep(520); break;
        case "tile_drawn": break;
        case "player_passed": toast(m.player_id === uid ? "Você passou a vez." : "Adversário passou."); break;
        case "round_finished":
          setSelected(null);
          setRoundEnd({ won: m.you_scored, points: m.round_points, reason: m.reason, scores: m.scores, target: m.points_target, matchOver: m.match_over, round: m.round });
          if (soundRef.current) beep(m.you_scored ? 640 : 300);
          break;
        case "new_round":
          setRoundEnd(null); setSelected(null); toast(`Rodada ${m.round} — nova mão distribuída`);
          break;
        case "chat_message": setChat((c) => [...c, m]); break;
        case "opponent_disconnected": setOppOffline(true); toast.warning("Adversário caiu. O jogo segue em modo automático até ele voltar."); break;
        case "opponent_reconnected": setOppOffline((prev) => { if (prev) toast.success("Adversário reconectou."); return null; }); break;
        case "balance": refreshRef.current?.(); break;
        case "game_finished":
          inMatchRef.current = false;
          finishedRef.current = true;
          setRoundEnd(null);
          setState(m.state); setPhase("finished");
          setResult({ won: m.you_won, reason: m.reason, prize: m.prize, is_bot: m.is_bot });
          if (soundRef.current) beep(m.you_won ? 880 : 220);
          refreshRef.current?.();
          break;
        case "error": toast.error(m.message); break;
        case "rematch_requested": setRematch((r) => (r === "waiting" ? r : "requested")); if (soundRef.current) beep(700); toast("Adversário quer revanche!"); break;
        case "rematch_waiting": setRematch("waiting"); break;
        case "rematch_declined":
        case "rematch_unavailable": setRematch(null); toast.info(m.message || "Revanche indisponível."); break;
        default: break;
      }
    };
  }, [navigate, send, stake, currency, pmode, pStake, pCurrency, pName, pPassword, pCode]);

  useEffect(() => {
    connect();
    return () => { closingRef.current = true; wsRef.current?.close(); };
  }, [connect]);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 400); return () => clearInterval(t); }, []);

  // Saída automática da mesa 3s após o fim, sem revanche — vale p/ Reais, Fichas e Robô (cancelada se houver revanche)
  useEffect(() => {
    if (!result || rematch) { setExitIn(null); return undefined; }
    setExitIn(3);
    const iv = setInterval(() => setExitIn((x) => (x != null && x > 1 ? x - 1 : x)), 1000);
    const to = setTimeout(() => { closingRef.current = true; wsRef.current?.close(); navigate("/lobby"); }, 3000);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, [result, rematch, navigate]);

  // derived
  const you = state?.you;
  const opp = state?.opponent;
  const yourTurn = state && state.current_turn === you?.user_id;
  const boardEmpty = state?.left_end === null || state?.left_end === undefined;
  const sidesFor = (tile) => {
    if (boardEmpty) return ["right"];
    const s = [];
    if (tile.left === state.left_end || tile.right === state.left_end) s.push("left");
    if (tile.left === state.right_end || tile.right === state.right_end) s.push("right");
    return s;
  };
  const canPlayTile = (t) => sidesFor(t).length > 0;
  const hasAnyPlay = you?.hand?.some(canPlayTile);
  const canDraw = yourTurn && state?.boneyard_count > 0 && !hasAnyPlay && phase === "playing";
  const canPass = yourTurn && state?.boneyard_count === 0 && !hasAnyPlay && phase === "playing";
  const youAuto = state?.you?.auto && phase === "playing";
  const resume = () => send({ action: "resume" });

  const secsLeft = state?.deadline ? Math.max(0, Math.ceil((new Date(state.deadline).getTime() - now) / 1000)) : null;
  const isVirtual = (state?.currency || currency) === "virtual";
  const cfmt = (n) => (isVirtual ? `${Number(n || 0).toLocaleString("pt-BR")} fichas` : money(n));

  const clickTile = (tile) => {
    if (!yourTurn || phase !== "playing") return;
    const sides = sidesFor(tile);
    if (sides.length === 0) { toast.error("Essa pedra não encaixa."); return; }
    // Sempre 2 toques: 1º seleciona a pedra, 2º indica o lado (mesmo com 1 opção) — evita jogada acidental
    setSelected(selected?.id === tile.id ? null : tile);
  };
  const playSide = (side) => { if (selected) { send({ action: "play", tile_id: selected.id, side }); setSelected(null); } };

  // Modo deslizante: pressione a pedra e arraste até a ponta escolhida da mesa
  const dropSideAt = (tile, x, y) => {
    const r = boardRef.current?.getBoundingClientRect();
    if (!r || x < r.left || x > r.right || y < r.top || y > r.bottom) return null;
    const sides = sidesFor(tile);
    if (sides.length === 0) return null;
    if (boardEmpty || sides.length === 1) return sides[0];
    return x < r.left + r.width / 2 ? "left" : "right";
  };
  const startDrag = (tile) => (e) => {
    if (!yourTurn || phase !== "playing") return;
    if (sidesFor(tile).length === 0) { toast.error("Essa pedra não encaixa."); return; }
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const d = { tile, x: e.clientX, y: e.clientY, over: null, moved: false, startX: e.clientX, startY: e.clientY };
    dragRef.current = d; setDrag(d);
  };
  const moveDrag = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const moved = d.moved || Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 6;
    const nd = { ...d, x: e.clientX, y: e.clientY, moved, over: dropSideAt(d.tile, e.clientX, e.clientY) };
    dragRef.current = nd; setDrag(nd);
  };
  const endDrag = (e) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null; setDrag(null);
    if (!d.moved) { clickTile(d.tile); return; }
    const side = dropSideAt(d.tile, e.clientX, e.clientY);
    if (side) { send({ action: "play", tile_id: d.tile.id, side }); setSelected(null); }
  };
  const cancelDrag = () => { dragRef.current = null; setDrag(null); };
  const handSize = typeof window !== "undefined" && window.innerWidth >= 640 ? 92 : 76;

  const abandon = () => {
    send({ action: "leave_match" });
    if (phase === "playing") toast.error("Você abandonou a partida e perdeu a aposta.");
    else if (phase === "queue") {
      if (privateCode || pmode === "create_private") send({ action: "cancel_private_room" });
      else if (stake) send({ action: "leave_queue", stake: parseFloat(stake), currency });
    }
    closingRef.current = true; wsRef.current?.close();
    navigate("/lobby");
  };

  const sendChat = () => { if (chatText.trim()) { send({ action: "chat", text: chatText }); setChatText(""); } };
  const requestRematch = () => send({ action: "rematch" });

  // ---- render ----
  if (phase === "connecting" || phase === "queue") {
    return (
      <div className="dr-grain min-h-screen bg-[#090B0E] flex items-center justify-center p-6">
        <div className="dr-card p-10 text-center max-w-sm w-full" data-testid="matchmaking">
          <Loader2 className="animate-spin mx-auto text-[#D4AF37]" size={48} />
          <h1 className="font-head font-black text-2xl mt-5">{privateCode ? "Sala privada criada" : pmode === "bot" ? "Preparando mesa vs Robô" : "Procurando adversário"}</h1>
          {privateCode ? (
            <div className="mt-4" data-testid="private-code">
              <p className="text-[#9CA3AF] text-sm">Compartilhe este código com seu amigo:</p>
              <div className="mt-2 flex items-center justify-center gap-2">
                <span className="font-mono-num font-black text-3xl gold-text tracking-widest">{privateCode}</span>
                <button onClick={() => { navigator.clipboard?.writeText(privateCode); toast.success("Código copiado!"); }} className="text-[#9CA3AF] hover:text-white" title="Copiar"><Copy size={18} /></button>
              </div>
              <p className="text-[11px] text-[#6b7280] mt-2">Aguardando o adversário entrar com o código{pPassword ? " e a senha" : ""}...</p>
            </div>
          ) : (
            <p className="text-[#9CA3AF] mt-2">{pmode === "join_private" ? "Entrando na sala privada..." : pmode === "bot" ? "Embaralhando as pedras contra o robô..." : `Sala de ${currency === "virtual" ? `${Number(stake).toLocaleString("pt-BR")} fichas` : `R$ ${stake}`} • aguardando outro jogador entrar...`}</p>
          )}
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-[#9CA3AF]">
            {connected ? <><Wifi size={14} className="text-[#10B981]" /> Conectado</> : <><WifiOff size={14} className="text-[#F59E0B]" /> Reconectando</>}
          </div>
          <button onClick={abandon} data-testid="cancel-queue-btn" className="mt-6 px-6 py-2.5 rounded-full border border-white/15 text-[#9CA3AF] hover:text-white transition-colors">Cancelar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dr-grain fixed inset-0 bg-[#090B0E] overflow-hidden" data-testid="game-table">
      {/* MESA em tela cheia (peças e informações sobrepostas ao feltro) */}
      <div className="absolute inset-x-1 top-[44px] bottom-[168px] sm:bottom-[184px] rounded-2xl felt overflow-hidden">
        <div className="absolute inset-0" data-testid="board" ref={boardRef}>
          <DominoBoard board={state?.board || []} />
        </div>
        {drag && (
          <div className="absolute inset-0 z-20 pointer-events-none flex" data-testid="drop-zones">
            {(boardEmpty || sidesFor(drag.tile).length === 1) ? (
              <div className={`flex-1 m-2 rounded-xl border-2 border-dashed transition-colors ${drag.over ? "border-[#10B981] bg-[#10B981]/15" : "border-[#D4AF37]/50 bg-[#D4AF37]/5"}`} data-testid="drop-zone-any" />
            ) : (
              <>
                <div className={`flex-1 m-2 rounded-xl border-2 border-dashed transition-colors ${drag.over === "left" ? "border-[#10B981] bg-[#10B981]/15" : "border-[#D4AF37]/50 bg-[#D4AF37]/5"}`} data-testid="drop-zone-left" />
                <div className={`flex-1 m-2 rounded-xl border-2 border-dashed transition-colors ${drag.over === "right" ? "border-[#10B981] bg-[#10B981]/15" : "border-[#D4AF37]/50 bg-[#D4AF37]/5"}`} data-testid="drop-zone-right" />
              </>
            )}
          </div>
        )}
        {!boardEmpty && (
          <div className="absolute top-2 right-3 z-10 text-[10px] uppercase tracking-widest text-white/60 font-head pointer-events-none">Pontas <span className="font-mono-num text-[#F3E5AB]">{state?.left_end}</span>/<span className="font-mono-num text-[#F3E5AB]">{state?.right_end}</span></div>
        )}

        {/* adversário sobreposto (topo do feltro) */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1">
          <PlayerBadge p={opp} active={!yourTurn} secs={!yourTurn ? secsLeft : null} offline={oppOffline} />
          <div className="flex gap-0.5" data-testid="opponent-hand">
            {Array.from({ length: opp?.hand_count || 0 }).map((_, i) => (
              <div key={i} className="w-4 h-7 rounded-sm" style={{ background: "linear-gradient(135deg,#B8860B,#7a5a10)", boxShadow: "0 2px 5px rgba(0,0,0,0.5)" }} />
            ))}
          </div>
        </div>

        {/* cava (pedras viradas, tocáveis para comprar) */}
        {state?.boneyard_count > 0 && (
          <div className="absolute bottom-2 left-2 z-10 flex items-end" data-testid="boneyard-pile">
            <div className="flex items-end">
              {Array.from({ length: Math.min(state.boneyard_count, 7) }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  disabled={!canDraw}
                  onClick={() => send({ action: "draw" })}
                  aria-label="Comprar pedra da cava"
                  data-testid={i === 0 ? "boneyard-tile" : undefined}
                  className={`w-6 h-11 sm:w-7 sm:h-12 rounded-md border border-[#5c4410] transition-transform duration-200 ${i === 0 ? "ml-0" : "-ml-4"} ${canDraw ? "cursor-pointer hover:-translate-y-1.5 ring-2 ring-[#D4AF37] pulse-green" : "cursor-default"}`}
                  style={{ background: "linear-gradient(135deg,#B8860B,#7a5a10)", boxShadow: "0 2px 6px rgba(0,0,0,0.5)" }}
                />
              ))}
            </div>
            <span className="ml-2 text-[10px] uppercase tracking-widest text-white/60 font-head">Cava <span className="font-mono-num text-[#F3E5AB]">{state.boneyard_count}</span></span>
          </div>
        )}
      </div>

      {/* TOP BAR sobreposto */}
      <div className="absolute top-0 inset-x-0 z-30 glass border-b border-[#D4AF37]/15 px-3 py-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs sm:text-sm min-w-0 truncate">
          {state?.is_bot ? (
            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#10B981]/20 border border-[#10B981]/50 text-[#6EE7B7] font-bold whitespace-nowrap" data-testid="bot-badge">
              <Bot size={13} /> vs Robô · <span className="text-[#F3E5AB]">+{state?.bot_reward || 50}</span>
            </span>
          ) : (
            <span className="font-mono-num text-[#D4AF37] font-bold whitespace-nowrap">{isVirtual ? `${Number(state?.stake).toLocaleString("pt-BR")} fichas` : `R$ ${state?.stake}`}</span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0" data-testid="scoreboard">
          <div className="text-center leading-tight"><div className="text-[8px] uppercase text-[#9CA3AF]">Você</div><div className="font-mono-num font-black text-lg text-[#10B981]">{state?.scores?.you ?? 0}</div></div>
          <div className="text-center leading-tight px-1"><div className="text-[8px] uppercase text-[#6b7280]">R{state?.round ?? 1}</div><div className="font-head text-[10px] text-[#D4AF37]">{state?.points_target ?? 50}pts</div></div>
          <div className="text-center leading-tight"><div className="text-[8px] uppercase text-[#9CA3AF]">Adv</div><div className="font-mono-num font-black text-lg text-[#EF4444]">{state?.scores?.opponent ?? 0}</div></div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="flex items-center gap-1 text-xs">{connected ? <Wifi size={14} className="text-[#10B981]" /> : <WifiOff size={14} className="text-[#F59E0B]" />}</span>
          <button onClick={() => setSound((s) => !s)} data-testid="sound-toggle" className="text-[#9CA3AF] hover:text-white">{sound ? <Volume2 size={17} /> : <VolumeX size={17} />}</button>
          <button onClick={abandon} data-testid="abandon-btn" className="flex items-center gap-1 text-xs text-[#EF4444] font-semibold"><LogOut size={15} /> Sair</button>
        </div>
      </div>

      {/* BOTTOM sobreposto: modo auto + confirmar jogada + ações + sua mão */}
      <div className="absolute bottom-0 inset-x-0 z-30">
        {youAuto && (
          <div className="mx-2 mb-1 flex items-center justify-between gap-3 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/15 backdrop-blur px-4 py-2" data-testid="auto-mode-banner">
            <span className="text-sm text-[#FCD34D] font-semibold">Modo automático ativo</span>
            <button onClick={resume} data-testid="resume-play-btn" className="btn-gold px-4 py-1.5 rounded-full text-xs whitespace-nowrap">Voltar a jogar</button>
          </div>
        )}
        {selected && (() => {
          const s = sidesFor(selected);
          const one = boardEmpty || s.length === 1;
          return (
            <div className="mx-2 mb-1 flex items-center justify-center gap-3 rounded-xl bg-black/50 backdrop-blur px-3 py-2" data-testid="side-chooser">
              <span className="text-xs text-[#9CA3AF]">Confirmar jogada:</span>
              {one ? (
                <button onClick={() => playSide(s[0])} className="btn-gold px-5 py-2 rounded-full text-sm" data-testid="play-confirm">✓ Jogar pedra</button>
              ) : (
                <>
                  <button onClick={() => playSide("left")} className="btn-gold px-4 py-2 rounded-full text-sm" data-testid="play-left">⬅ Esquerda</button>
                  <button onClick={() => playSide("right")} className="btn-gold px-4 py-2 rounded-full text-sm" data-testid="play-right">Direita ➡</button>
                </>
              )}
              <button onClick={() => setSelected(null)} className="px-3 py-2 rounded-full border border-white/15 text-xs text-[#9CA3AF]" data-testid="play-cancel">Cancelar</button>
            </div>
          );
        })()}
        <div className="glass border-t border-[#D4AF37]/15 px-2 pt-1.5 pb-1.5">
          <div className="flex items-center justify-center gap-2 flex-wrap mb-1.5">
            {yourTurn && <span className="text-[#10B981] font-head font-bold text-sm pulse-green px-3 py-1 rounded-full bg-[#10B981]/10" data-testid="turn-indicator">SUA VEZ {secsLeft != null && `• ${String(secsLeft).padStart(2, "0")}s`}</span>}
            {!yourTurn && phase === "playing" && <span className="text-[#9CA3AF] text-sm">Vez do adversário{secsLeft != null && ` • ${String(secsLeft).padStart(2, "0")}s`}</span>}
            <button onClick={() => send({ action: "draw" })} disabled={!canDraw} data-testid="draw-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#151B24] border border-[#D4AF37]/30 text-sm font-semibold disabled:opacity-40 enabled:hover:border-[#D4AF37]/60 transition-colors"><ShoppingCart size={15} /> Comprar</button>
            <button onClick={() => send({ action: "pass" })} disabled={!canPass} data-testid="pass-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#151B24] border border-white/10 text-sm font-semibold disabled:opacity-40 enabled:hover:border-white/30 transition-colors"><SkipForward size={15} /> Passar</button>
          </div>
          <div className="flex justify-center gap-2 flex-wrap max-w-full touch-none select-none" data-testid="your-hand">
            {you?.hand?.map((t) => (
              <div key={t.id} onPointerDown={startDrag(t)} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={cancelDrag}
                className={`touch-none ${drag?.tile?.id === t.id ? "opacity-30" : ""}`} data-testid={`hand-slot-${t.id}`}>
                <DominoTile left={t.left} right={t.right} orientation="v" size={handSize}
                  playable={yourTurn && canPlayTile(t)} selected={selected?.id === t.id}
                  dim={yourTurn && !canPlayTile(t)} onClick={() => {}} testid={`hand-tile-${t.id}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {drag?.moved && (
        <div className="fixed z-50 pointer-events-none" style={{ left: drag.x, top: drag.y, transform: "translate(-50%,-50%) rotate(-6deg) scale(1.08)" }} data-testid="drag-ghost">
          <DominoTile left={drag.tile.left} right={drag.tile.right} orientation="v" size={handSize} playable />
        </div>
      )}

      {/* chat button + drawer */}
      <button onClick={() => setShowChat((s) => !s)} data-testid="chat-toggle"
        className="fixed bottom-24 right-4 z-40 w-12 h-12 rounded-full btn-gold flex items-center justify-center shadow-lg">
        <Send size={18} />
      </button>
      {showChat && (
        <div className="fixed bottom-40 right-4 z-40 w-72 dr-card p-3 glass" data-testid="chat-box">
          <div className="h-40 overflow-y-auto space-y-1.5 mb-2 text-sm">
            {chat.length === 0 ? <p className="text-[#6b7280] text-xs">Sem mensagens.</p> : chat.map((c, i) => (
              <div key={i}><span className="font-semibold text-[#F3E5AB]">{c.name}:</span> <span className="text-[#d1d5db]">{c.text}</span></div>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={chatText} onChange={(e) => setChatText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()}
              placeholder="Mensagem..." data-testid="chat-input" className="flex-1 bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
            <button onClick={sendChat} className="btn-gold px-3 rounded-lg" data-testid="chat-send"><Send size={15} /></button>
          </div>
        </div>
      )}

      {/* round-end overlay */}
      {roundEnd && !result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-6" data-testid="round-end-overlay">
          <div className="dr-card p-8 text-center max-w-sm w-full fade-up">
            <div className="text-xs uppercase tracking-widest text-[#9CA3AF]">Fim da rodada {roundEnd.round}</div>
            <h2 className={`font-head font-black text-2xl mt-2 ${roundEnd.won ? "text-[#10B981]" : "text-[#EF4444]"}`}>
              {roundEnd.won ? "Você marcou!" : "Adversário marcou"}
            </h2>
            <p className="text-[#9CA3AF] mt-1 text-sm">
              {roundEnd.reason === "domino" ? "Batida" : roundEnd.reason === "block" ? "Jogo fechado" : "Rodada"} • +{roundEnd.points} pts
            </p>
            <div className="flex items-center justify-center gap-6 mt-5">
              <div><div className="text-[10px] text-[#9CA3AF] uppercase">Você</div><div className="font-mono-num font-black text-3xl text-[#10B981]">{roundEnd.scores?.you}</div></div>
              <div className="text-[#6b7280] font-head">x</div>
              <div><div className="text-[10px] text-[#9CA3AF] uppercase">Adv</div><div className="font-mono-num font-black text-3xl text-[#EF4444]">{roundEnd.scores?.opponent}</div></div>
            </div>
            <p className="text-xs text-[#6b7280] mt-4">Meta: {roundEnd.target} pts • próxima rodada em instantes...</p>
          </div>
        </div>
      )}

      {/* result overlay */}
      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6">
          <div className="dr-card p-10 text-center max-w-sm w-full fade-up" data-testid="result-overlay">
            <Trophy size={56} className={`mx-auto ${result.won ? "text-[#FFD700]" : "text-[#6b7280]"}`} />
            <h1 className="font-head font-black text-3xl mt-4">{result.won ? "Você venceu!" : "Você perdeu"}</h1>
            <p className="text-[#9CA3AF] mt-2">
              {result.reason === "domino" ? "Bateu o dominó!" : result.reason === "block" ? "Jogo fechado." : result.reason === "abandono" ? "Partida encerrada por abandono." : result.reason === "pontos" ? `Atingiu ${state?.points_target ?? 50} pontos!` : "Fim de jogo."}
            </p>
            <p className="font-mono-num text-sm text-[#9CA3AF] mt-2">Placar final: {state?.scores?.you ?? 0} x {state?.scores?.opponent ?? 0}</p>
            {result.won && result.prize > 0 && <p className="font-mono-num text-2xl gold-text mt-3">+{cfmt(result.prize)}</p>}
            {rematch === "requested" && <div className="mt-4 text-sm text-[#FCD34D] font-semibold" data-testid="rematch-requested-msg">O adversário quer revanche!</div>}
            {rematch === "waiting" && <div className="mt-4 flex items-center justify-center gap-2 text-sm text-[#9CA3AF]" data-testid="rematch-waiting-msg"><Loader2 size={14} className="animate-spin" /> Aguardando o adversário aceitar...</div>}
            <div className="flex gap-3 mt-6">
              <button onClick={abandon} data-testid="back-lobby-btn" className="flex-1 py-3 rounded-full border border-white/15">Lobby{exitIn != null ? ` (${exitIn}s)` : ""}</button>
              {rematch === "waiting" ? (
                <button disabled className="flex-1 btn-gold py-3 rounded-full opacity-60">Aguardando...</button>
              ) : (
                <button onClick={requestRematch} data-testid="rematch-btn" className="flex-1 btn-gold py-3 rounded-full">{result?.is_bot ? "Jogar de novo" : rematch === "requested" ? "Aceitar revanche" : "Revanche"}</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlayerBadge({ p, active, secs, you, offline }) {
  if (!p) return null;
  return (
    <div className={`flex items-center gap-3 px-4 py-2 rounded-2xl transition-colors ${active ? "bg-[#10B981]/10 border border-[#10B981]/40" : "bg-[#151B24] border border-white/5"}`}>
      <div className="relative">
        <img src={p.avatar || "https://api.dicebear.com/7.x/initials/svg?seed=" + p.name} className="w-11 h-11 rounded-full border-2 border-[#D4AF37]/40 object-cover" alt="" />
        {active && secs != null && (
          <span className={`absolute -bottom-1 -right-1 text-[10px] font-mono-num font-bold px-1.5 py-0.5 rounded-full ${secs <= 5 ? "bg-[#EF4444]" : "bg-[#10B981]"} text-black`}>{secs}</span>
        )}
      </div>
      <div>
        <div className="font-head font-bold text-sm flex items-center gap-1">{p.name} {you && <span className="text-[10px] text-[#9CA3AF]">(você)</span>}</div>
        <div className="text-[11px] text-[#9CA3AF]">Nível {p.level} • {p.hand_count} pedras {offline ? "• 🔴 offline" : ""}</div>
      </div>
    </div>
  );
}
